import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/contexts/AuthContext';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { user, logout, emailConfirmed } = useAuthContext();

  // If email just got confirmed, redirect to dashboard
  useEffect(() => {
    if (emailConfirmed) {
      navigate('/dashboard', { replace: true });
    }
  }, [emailConfirmed, navigate]);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    if (!user?.email) return;
    setError('');
    setResending(true);
    const { error: resendError } = await supabase.auth.resend({
      type: 'signup',
      email: user.email,
    });
    setResending(false);
    if (resendError) {
      setError(resendError.message);
    } else {
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/d3c3ec87-e7de-4a4f-b250-82f788f182b3.png"
            alt="API MART"
            className="w-14 h-14 rounded-xl mx-auto mb-4 object-cover"
          />
          <h1 className="text-2xl font-bold text-white">API MART</h1>
          <p className="text-white/40 text-sm mt-1">VERIFY YOUR EMAIL</p>
        </div>

        <div className="bg-white rounded-2xl p-6 text-center">
          <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <i className="ri-mail-send-line text-brand-500 text-3xl" />
          </div>

          <h2 className="text-lg font-semibold text-gray-900 mb-2">Verify your email</h2>
          <p className="text-sm text-gray-500 leading-relaxed mb-1">
            We sent a confirmation link to
          </p>
          <p className="text-sm font-semibold text-gray-800 mb-5">{user?.email}</p>

          <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5 text-left">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700 leading-relaxed">
                Click the link in the email to activate your account. Check your spam folder if you don&apos;t see it.
              </p>
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5 text-left"
            >
              <i className="ri-error-warning-fill text-red-500 shrink-0" />
              {error}
            </motion.div>
          )}

          {resent && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2.5 text-left"
            >
              <i className="ri-checkbox-circle-fill text-green-500 shrink-0" />
              Verification email resent! Check your inbox.
            </motion.div>
          )}

          <button
            onClick={handleResend}
            disabled={resending}
            className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg text-sm cursor-pointer whitespace-nowrap transition-colors mb-3"
          >
            {resending ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin" />Resending...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-refresh-line" />Resend Verification Email
              </span>
            )}
          </button>

          <button
            onClick={handleLogout}
            className="w-full text-xs text-gray-400 hover:text-gray-600 cursor-pointer transition-colors py-1"
          >
            <i className="ri-logout-box-r-line mr-1" />Sign out and use a different account
          </button>
        </div>
      </motion.div>
    </div>
  );
}
